import React, { useState } from 'react';
import {
  Badge, Box, Button, Heading, HStack, Spacer, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiCreditCard } from 'react-icons/fi';
import GlassCard from './GlassCard';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import AutoPayModal from './AutoPayModal';
import { RecurringRevenue, RecurringRevenueStatus } from '../models/recurringRevenue';
import useCurrentUser from '../hooks/useCurrentUser';
import useStripeSubscriptionIdentities from '../hooks/useStripeSubscriptionIdentities'; // item 1599
import { enrolledDate, enrolledDisplay, enrolledTitle } from '../utils/enrolledDate'; // item 1599

// item 728 (Chad) — active subscriptions NOT on auto-pay (still billed by emailed invoice). Auto-pay
// is opt-in, never forced — this lists who to work on enrolling (or send the self-serve link). They
// also still appear in the master Subscriptions list below.
const RecurringAutoPaySection: React.FC<{ rows: RecurringRevenue[] }> = ({ rows }) => {
  const { isAdmin } = useCurrentUser();
  const [autoPayId, setAutoPayId] = useState<string | null>(null);
  const border = useColorModeValue('blue.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const identities = useStripeSubscriptionIdentities(); // item 1599
  const enrolledOf = (r: RecurringRevenue) => enrolledDate(r.startDate, identities[r.stripeSubscriptionId ?? '']?.startDate);

  // live Stripe subscription, active, not yet on auto-pay
  const pending = rows.filter((r) => r.status === RecurringRevenueStatus.Active
    && !r.isArchive && !r.autoPayEnrolled
    && (r.stripeSubscriptionId ?? '').startsWith('sub_'));

  if (!isAdmin || pending.length === 0) return null;

  // item 732 (Chad) — standardized ledger style (aligned columns), same as the Subscriptions grid
  const columns: LedgerColumn<RecurringRevenue>[] = [
    {
      // item 1445 (Chad: "a pass on all the ledgers … the name at the bottom under the other items like the portal") —
      // SUBSCRIPTION = name bold, customer · project as subtext (CUSTOMER / PROJECT columns folded in).
      // item 1447 — name + project ride the full-width rowSubline; the customer is the cell.
      key: 'name',
      header: 'SUBSCRIPTION',
      width: '180px',
      sortValue: (r) => r.name ?? '',
      render: (r) => <Text fontWeight="semibold" noOfLines={1}>{r.customerName || r.name || '—'}</Text>,
    },
    {
      // item 1599 — first enrolled (Stripe start) or contract billing start, whichever is earlier
      key: 'started',
      header: 'ENROLLED',
      width: '100px',
      sortValue: (r) => enrolledOf(r)?.date ?? '',
      render: (r) => {
        const e = enrolledOf(r);
        return <Text whiteSpace="nowrap" title={enrolledTitle(e)}>{enrolledDisplay(e)}</Text>;
      },
    },
    {
      key: 'billing',
      header: 'BILLING',
      width: '160px',
      sortValue: () => 'EMAILED INVOICE',
      render: () => <Badge colorScheme="blue">EMAILED INVOICE</Badge>,
    },
  ];

  const rowActions = (r: RecurringRevenue) => (
    <Button size="xs" leftIcon={<FiCreditCard />} onClick={() => setAutoPayId(r.id)}>
      Auto-pay
    </Button>
  );

  return (
    <>
      <GlassCard p={{ base: 4, md: 5 }} borderColor={border} borderWidth="1px">
        <HStack mb={1.5}>
          <Box color="blue.400"><FiCreditCard /></Box>
          <Heading size="sm">Not on auto-pay</Heading>
          <Badge colorScheme="blue" variant="solid" borderRadius="sm">{pending.length}</Badge>
          <Spacer />
        </HStack>
        <Text fontSize="xs" color={sub} mb={3}>
          Active but billed by emailed invoice (client pays by ACH). Enroll in auto-pay once a bank is
          on file, or send the self-serve link. Auto-pay is never forced.
        </Text>
        <LedgerTable<RecurringRevenue>
          rows={pending}
          columns={columns}
          rowSubline={(r) => <LedgerSubline name={r.name} meta={[r.projectName]} />}
          rowId={(r) => r.id}
          rowActions={rowActions}
          rowActionsHeader=""
          rowActionsWidth="130px"
          selectable={false}
          emptyText="All active subscriptions are on auto-pay."
        />
      </GlassCard>
      <AutoPayModal recurringId={autoPayId} onClose={() => setAutoPayId(null)} />
    </>
  );
};

export default RecurringAutoPaySection;
